import { useRef, useState } from 'react';
import { Bot, Send, User, Loader2, FileDown, FileSpreadsheet } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { API_BASE } from '../../utils/api-base';

interface OperationsCopilotProps {
  clientId: string;
  token: string;
}

interface ToolResult {
  name: string;
  input: Record<string, unknown>;
  result: unknown;
}

interface ChatMessage {
  role: 'user' | 'assistant' | 'error';
  text: string;
  question?: string; // the user question this answer responds to — needed to export it
  toolResults?: ToolResult[];
}

const SUGGESTIONS = [
  'What alarms are active right now?',
  'Why do our work orders keep failing?',
  'Summarize energy usage this week',
];

export function OperationsCopilot({ clientId, token }: OperationsCopilotProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [asking, setAsking] = useState(false);
  const [exportingKey, setExportingKey] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const ask = async (question: string) => {
    const q = question.trim();
    if (!q || asking) return;
    setMessages((prev) => [...prev, { role: 'user', text: q }]);
    setInput('');
    setAsking(true);
    try {
      const res = await fetch(`${API_BASE}/api/clients/${clientId}/copilot/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ question: q }),
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.error || 'The copilot could not answer that.');
      setMessages((prev) => [...prev, { role: 'assistant', text: payload.answer, question: q, toolResults: payload.toolResults || [] }]);
    } catch (err: any) {
      setMessages((prev) => [...prev, { role: 'error', text: err?.message || 'Something went wrong.' }]);
    } finally {
      setAsking(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  };

  const hasExportableData = (m: ChatMessage) =>
    (m.toolResults || []).some((tr) => Array.isArray(tr.result) && tr.result.length > 0);

  const downloadExport = async (m: ChatMessage, index: number, format: 'pdf' | 'excel') => {
    const key = `${index}-${format}`;
    setExportingKey(key);
    try {
      const res = await fetch(`${API_BASE}/api/clients/${clientId}/copilot/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ question: m.question, answer: m.text, toolResults: m.toolResults, format }),
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `copilot-answer.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setMessages((prev) => [...prev, { role: 'error', text: 'Could not generate that file. Try again.' }]);
    } finally {
      setExportingKey(null);
    }
  };

  return (
    <div className="p-6 flex flex-col h-full max-h-[calc(100vh-4rem)]">
      <div className="mb-4">
        <h2 className="text-xl font-semibold flex items-center gap-2"><Bot className="h-5 w-5" /> Operations Copilot</h2>
        <p className="text-sm text-muted-foreground">Answers are grounded in this client's own alarms, work orders, and energy data — nothing is invented.</p>
      </div>

      <div className="flex-1 overflow-y-auto border rounded-lg p-4 space-y-4 bg-muted/20">
        {messages.length === 0 && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Try asking:</p>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => ask(s)}
                className="block w-full text-left text-sm border rounded-md px-3 py-2 hover:bg-muted transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {m.role !== 'user' && (
              <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Bot className="h-4 w-4 text-primary" />
              </div>
            )}
            <div className={`max-w-[75%] flex flex-col gap-1.5 ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
              <div
                className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === 'user' ? 'bg-primary text-primary-foreground'
                  : m.role === 'error' ? 'bg-destructive/10 text-destructive'
                  : 'bg-background border'
                }`}
              >
                {m.text}
              </div>
              {m.role === 'assistant' && hasExportableData(m) && (
                <div className="flex gap-1.5">
                  <Button
                    variant="outline" size="sm" className="h-6 text-xs px-2"
                    disabled={exportingKey === `${i}-pdf`}
                    onClick={() => downloadExport(m, i, 'pdf')}
                  >
                    <FileDown className="h-3 w-3 mr-1" /> {exportingKey === `${i}-pdf` ? 'Preparing...' : 'Download PDF'}
                  </Button>
                  <Button
                    variant="outline" size="sm" className="h-6 text-xs px-2"
                    disabled={exportingKey === `${i}-excel`}
                    onClick={() => downloadExport(m, i, 'excel')}
                  >
                    <FileSpreadsheet className="h-3 w-3 mr-1" /> {exportingKey === `${i}-excel` ? 'Preparing...' : 'Download Excel'}
                  </Button>
                </div>
              )}
            </div>
            {m.role === 'user' && (
              <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                <User className="h-4 w-4" />
              </div>
            )}
          </div>
        ))}
        {asking && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking the data...
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="flex gap-2 mt-3">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') ask(input); }}
          placeholder="Ask about alarms, work orders, or energy use..."
          disabled={asking}
        />
        <Button onClick={() => ask(input)} disabled={asking || !input.trim()}>
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
